// v10/src/components/admin/AdminFlightAuth.js.
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Shield, CheckCircle2, XCircle, Clock, Plane,
  AlertTriangle, RefreshCw, Loader, ChevronDown, ChevronUp, X,
} from 'lucide-react';
import api from '../../services/apiService';
import { POLLING_INTERVALS } from '../../config/realtime';

const formatDateTime = (d) => d
  ? new Date(d).toLocaleString('es-AR', { day:'2-digit', month:'2-digit', year:'numeric', hour:'2-digit', minute:'2-digit' })
  : '—';

const formatTimeAgo = (d) => {
  if (!d) return '—';
  const diff = Math.floor((Date.now() - new Date(d)) / 1000);
  if (diff < 60)  return `hace ${diff}s`;
  if (diff < 3600) return `hace ${Math.floor(diff/60)}min`;
  return `hace ${Math.floor(diff/3600)}hs`;
};

// ── Countdown timer ───────────────────────────────────────────────
const Countdown = ({ createdAt, timeoutMinutes, darkMode }) => {
  const [left, setLeft] = useState(0);

  useEffect(() => {
    const end = new Date(createdAt).getTime() + timeoutMinutes * 60 * 1000;
    const tick = () => {
      const remaining = Math.max(0, Math.floor((end - Date.now()) / 1000));
      setLeft(remaining);
    };
    tick();
    const t = setInterval(tick, 1000);
    return () => clearInterval(t);
  }, [createdAt, timeoutMinutes]);

  const mins = Math.floor(left / 60);
  const secs = left % 60;
  const urgent = left < 120; // menos de 2 minutos

  return (
    <span style={{
      fontFamily: 'monospace', fontWeight: 800, fontSize: '13px',
      color: left === 0 ? '#94a3b8' : urgent ? '#ef4444' : '#d97706',
    }}>
      {left === 0 ? 'Expirado' : `${String(mins).padStart(2,'0')}:${String(secs).padStart(2,'0')}`}
    </span>
  );
};

// ── Modal de decisión ─────────────────────────────────────────────
const DecideModal = ({ auth, decision, onDecided, onClose, darkMode }) => {
  const { t } = useTranslation('admin');
  const [note, setNote]       = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState('');

  const isAuth   = decision === 'authorized';
  const color    = isAuth ? '#16a34a' : '#ef4444';
  const gradient = isAuth ? 'linear-gradient(135deg,#16a34a,#15803d)' : 'linear-gradient(135deg,#ef4444,#dc2626)';
  const icon     = isAuth ? <CheckCircle2 style={{ width:'28px', height:'28px', color:'white' }} /> : <XCircle style={{ width:'28px', height:'28px', color:'white' }} />;
  const title    = isAuth ? t('authorize_flight') : t('retain_aircraft');
  const label    = isAuth ? 'Nota para el piloto (opcional)' : 'Motivo del rechazo (requerido)';

  const handle = async () => {
    if (!isAuth && !note.trim()) { setError(t('rejection_reason_required')); return; }
    setLoading(true); setError('');
    try {
      await api.put(`/api/flight-auth/${auth.id}/decide`, {
        decision,
        note: note.trim() || null,
      });
      onDecided();
      onClose();
    } catch (err) {
      setError(err.response?.data?.message || t('error_processing'));
    } finally { setLoading(false); }
  };

  return (
    <motion.div initial={{ opacity:0 }} animate={{ opacity:1 }} exit={{ opacity:0 }}
      style={{ position:'fixed', inset:0, zIndex:100, display:'flex', alignItems:'center', justifyContent:'center', padding:'16px', background:'rgba(0,0,0,0.6)', backdropFilter:'blur(6px)' }}
      onClick={onClose}>
      <motion.div initial={{ scale:0.92, y:20 }} animate={{ scale:1, y:0 }} exit={{ scale:0.92, y:20 }}
        style={{ width:'100%', maxWidth:'440px', borderRadius:'24px', background: darkMode?'rgba(15,23,42,0.98)':'white', border: darkMode?'1px solid rgba(255,255,255,0.1)':'1px solid #e2e8f0', padding:'28px', boxShadow:'0 24px 60px rgba(0,0,0,0.35)' }}
        onClick={e => e.stopPropagation()}>

        <div style={{ textAlign:'center', marginBottom:'20px' }}>
          <div style={{ width:'56px', height:'56px', borderRadius:'50%', background: `${color}15`, border:`2px solid ${color}30`, display:'flex', alignItems:'center', justifyContent:'center', margin:'0 auto 12px', background: gradient }}>
            {icon}
          </div>
          <h3 style={{ margin:'0 0 4px', fontSize:'18px', fontWeight:800, color: darkMode?'white':'#0f172a' }}>{title}</h3>
          <p style={{ margin:0, fontSize:'13px', color: darkMode?'rgba(255,255,255,0.4)':'#64748b' }}>
            {auth.pilot_name} — {auth.aircraft_name}{auth.registration ? ` (${auth.registration})` : ''}
          </p>
        </div>

        <div style={{ marginBottom:'16px' }}>
          <p style={{ margin:'0 0 6px', fontSize:'12px', fontWeight:700, textTransform:'uppercase', letterSpacing:'0.06em', color: darkMode?'rgba(255,255,255,0.4)':'#94a3b8' }}>{label}</p>
          <textarea
            value={note} onChange={e => setNote(e.target.value)}
            placeholder={isAuth ? 'Ej: Todo verificado, podés volar' : 'Ej: Revisar el sistema de combustible antes de volar'}
            rows={3}
            style={{ width:'100%', padding:'10px 14px', borderRadius:'12px', border: darkMode?'1px solid rgba(255,255,255,0.12)':'1px solid #e2e8f0', background: darkMode?'rgba(255,255,255,0.05)':'#f8fafc', color: darkMode?'white':'#0f172a', fontSize:'14px', fontFamily:'inherit', outline:'none', boxSizing:'border-box', resize:'vertical', minHeight:'80px' }}
          />
        </div>

        {error && (
          <div style={{ padding:'10px 14px', borderRadius:'12px', background:'rgba(239,68,68,0.08)', border:'1px solid rgba(239,68,68,0.2)', marginBottom:'14px' }}>
            <p style={{ margin:0, fontSize:'13px', color:'#ef4444' }}>{error}</p>
          </div>
        )}

        <div style={{ display:'flex', gap:'10px' }}>
          <motion.button onClick={handle} disabled={loading}
            whileHover={!loading ? { scale:1.02 } : {}} whileTap={!loading ? { scale:0.97 } : {}}
            style={{ flex:1, padding:'13px', borderRadius:'14px', border:'none', cursor: loading?'not-allowed':'pointer', background: gradient, color:'white', fontWeight:700, fontSize:'14px', fontFamily:'inherit', display:'flex', alignItems:'center', justifyContent:'center', gap:'8px' }}>
            {loading ? <Loader style={{ width:'16px', height:'16px', animation:'spin 1s linear infinite' }} /> : isAuth ? <CheckCircle2 style={{ width:'16px', height:'16px' }} /> : <XCircle style={{ width:'16px', height:'16px' }} />}
            {loading ? 'Procesando...' : title}
          </motion.button>
          <button onClick={onClose}
            style={{ padding:'13px 18px', borderRadius:'14px', border: darkMode?'1px solid rgba(255,255,255,0.1)':'1px solid #e2e8f0', background:'transparent', cursor:'pointer', fontWeight:600, fontSize:'14px', color: darkMode?'rgba(255,255,255,0.5)':'#64748b', fontFamily:'inherit' }}>
            Cancelar
          </button>
        </div>
      </motion.div>
    </motion.div>
  );
};

// ── Card de autorización pendiente ────────────────────────────────
const AuthCard = ({ auth, onDecided, darkMode }) => {
  const [expanded, setExpanded]   = useState(true); // expandido por defecto
  const [deciding, setDeciding]   = useState(null);  // 'authorized' | 'retained'
  const observations              = auth.observations || [];
  const needsReview               = observations.filter(o => o.pilot_decision === 'needs_review');
  const canFly                    = observations.filter(o => o.pilot_decision === 'can_fly');

  return (
    <motion.div
      initial={{ opacity:0, y:12 }} animate={{ opacity:1, y:0 }}
      style={{ borderRadius:'20px', border:'1.5px solid rgba(245,158,11,0.35)', background: darkMode?'rgba(245,158,11,0.04)':'rgba(255,251,235,0.8)', overflow:'hidden', marginBottom:'12px', boxShadow:'0 4px 20px rgba(245,158,11,0.1)' }}>

      {/* Header urgente */}
      <div style={{ padding:'16px 20px', background:'linear-gradient(135deg,rgba(245,158,11,0.12),rgba(239,68,68,0.06))', borderBottom: darkMode?'1px solid rgba(255,255,255,0.06)':'1px solid rgba(245,158,11,0.2)', display:'flex', alignItems:'center', gap:'12px', cursor:'pointer' }}
        onClick={() => setExpanded(e => !e)}>

        {/* Pulso animado */}
        <div style={{ position:'relative', flexShrink:0 }}>
          <motion.div animate={{ scale:[1,1.4,1], opacity:[0.7,0,0.7] }} transition={{ duration:1.5, repeat:Infinity }}
            style={{ position:'absolute', inset:'-4px', borderRadius:'50%', background:'rgba(245,158,11,0.3)' }} />
          <div style={{ width:'36px', height:'36px', borderRadius:'50%', background:'linear-gradient(135deg,#f59e0b,#ef4444)', display:'flex', alignItems:'center', justifyContent:'center' }}>
            <Shield style={{ width:'18px', height:'18px', color:'white' }} />
          </div>
        </div>

        <div style={{ flex:1, minWidth:0 }}>
          <div style={{ display:'flex', alignItems:'center', gap:'8px', flexWrap:'wrap' }}>
            <p style={{ margin:0, fontWeight:800, fontSize:'15px', color: darkMode?'white':'#0f172a' }}>
              {auth.pilot_name}
            </p>
            <span style={{ fontSize:'11px', fontWeight:700, padding:'2px 8px', borderRadius:'20px', background:'rgba(245,158,11,0.15)', border:'1px solid rgba(245,158,11,0.3)', color:'#d97706', whiteSpace:'nowrap' }}>
              ⚠️ Autorización pendiente
            </span>
          </div>
          <p style={{ margin:'3px 0 0', fontSize:'12px', color: darkMode?'rgba(255,255,255,0.4)':'#64748b' }}>
            ✈️ {auth.aircraft_name}{auth.registration ? ` · ${auth.registration}` : ''} · {formatTimeAgo(auth.created_at)}
          </p>
        </div>

        {/* Countdown */}
        <div style={{ textAlign:'right', flexShrink:0 }}>
          <div style={{ display:'flex', alignItems:'center', gap:'4px', marginBottom:'2px' }}>
            <Clock style={{ width:'11px', height:'11px', color:'#d97706' }} />
            <span style={{ fontSize:'10px', fontWeight:600, color:'#d97706', textTransform:'uppercase', letterSpacing:'0.05em' }}>Tiempo</span>
          </div>
          <Countdown createdAt={auth.created_at} timeoutMinutes={auth.timeout_minutes || 10} darkMode={darkMode} />
        </div>

        {expanded
          ? <ChevronUp  style={{ width:'16px', height:'16px', color: darkMode?'rgba(255,255,255,0.3)':'#94a3b8', flexShrink:0 }} />
          : <ChevronDown style={{ width:'16px', height:'16px', color: darkMode?'rgba(255,255,255,0.3)':'#94a3b8', flexShrink:0 }} />
        }
      </div>

      {/* Detalle expandible */}
      <AnimatePresence>
        {expanded && (
          <motion.div initial={{ height:0, opacity:0 }} animate={{ height:'auto', opacity:1 }} exit={{ height:0, opacity:0 }} transition={{ duration:0.2 }}
            style={{ overflow:'hidden' }}>
            <div style={{ padding:'16px 20px' }}>

              {/* Anomalías que necesitan revisión */}
              {needsReview.length > 0 && (
                <div style={{ marginBottom:'14px' }}>
                  <p style={{ margin:'0 0 8px', fontSize:'11px', fontWeight:700, textTransform:'uppercase', letterSpacing:'0.06em', color:'#ef4444' }}>
                    {needsReview.length} anomalía{needsReview.length > 1 ? 's' : ''} que requiere{needsReview.length > 1 ? 'n' : ''} revisión
                  </p>
                  <div style={{ display:'flex', flexDirection:'column', gap:'6px' }}>
                    {needsReview.map((obs, i) => (
                      <div key={i} style={{ padding:'10px 12px', borderRadius:'12px', background:'rgba(239,68,68,0.07)', border:'1px solid rgba(239,68,68,0.2)', display:'flex', alignItems:'flex-start', gap:'8px' }}>
                        <AlertTriangle style={{ width:'14px', height:'14px', color:'#ef4444', flexShrink:0, marginTop:'1px' }} />
                        <div>
                          <p style={{ margin:0, fontSize:'13px', fontWeight:600, color: darkMode?'rgba(255,255,255,0.85)':'#0f172a' }}>{obs.text}</p>
                          {obs.comment && <p style={{ margin:'2px 0 0', fontSize:'11px', color: darkMode?'rgba(255,255,255,0.4)':'#64748b', fontStyle:'italic' }}>"{obs.comment}"</p>}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Anomalías menores (puede volar) */}
              {canFly.length > 0 && (
                <div style={{ marginBottom:'16px' }}>
                  <p style={{ margin:'0 0 6px', fontSize:'11px', fontWeight:700, textTransform:'uppercase', letterSpacing:'0.06em', color:'#16a34a' }}>
                    {canFly.length} anomalía{canFly.length > 1 ? 's' : ''} menor{canFly.length > 1 ? 'es' : ''} (piloto dice puede volar)
                  </p>
                  <div style={{ display:'flex', flexDirection:'column', gap:'4px' }}>
                    {canFly.map((obs, i) => (
                      <div key={i} style={{ padding:'8px 12px', borderRadius:'10px', background:'rgba(22,163,74,0.06)', border:'1px solid rgba(22,163,74,0.15)', display:'flex', alignItems:'flex-start', gap:'8px' }}>
                        <CheckCircle2 style={{ width:'13px', height:'13px', color:'#16a34a', flexShrink:0, marginTop:'1px' }} />
                        <p style={{ margin:0, fontSize:'12px', color: darkMode?'rgba(255,255,255,0.6)':'#374151' }}>{obs.text}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Botones de decisión */}
              <div style={{ display:'flex', gap:'10px' }}>
                <motion.button onClick={() => setDeciding('authorized')}
                  whileHover={{ scale:1.02, y:-1 }} whileTap={{ scale:0.97 }}
                  style={{ flex:1, padding:'13px', borderRadius:'14px', border:'none', cursor:'pointer', background:'linear-gradient(135deg,#16a34a,#15803d)', color:'white', fontWeight:700, fontSize:'14px', fontFamily:'inherit', display:'flex', alignItems:'center', justifyContent:'center', gap:'8px', boxShadow:'0 4px 16px rgba(22,163,74,0.3)' }}>
                  <CheckCircle2 style={{ width:'16px', height:'16px' }} />
                  Autorizar vuelo
                </motion.button>
                <motion.button onClick={() => setDeciding('retained')}
                  whileHover={{ scale:1.02, y:-1 }} whileTap={{ scale:0.97 }}
                  style={{ flex:1, padding:'13px', borderRadius:'14px', border:'none', cursor:'pointer', background:'linear-gradient(135deg,#ef4444,#dc2626)', color:'white', fontWeight:700, fontSize:'14px', fontFamily:'inherit', display:'flex', alignItems:'center', justifyContent:'center', gap:'8px', boxShadow:'0 4px 16px rgba(239,68,68,0.3)' }}>
                  <XCircle style={{ width:'16px', height:'16px' }} />
                  Retener avión
                </motion.button>
              </div>

              <p style={{ margin:'10px 0 0', textAlign:'center', fontSize:'11px', color: darkMode?'rgba(255,255,255,0.2)':'#94a3b8' }}>
                Solicitado {formatDateTime(auth.created_at)} · La decisión queda registrada con tu nombre
              </p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Modal de decisión */}
      <AnimatePresence>
        {deciding && (
          <DecideModal
            auth={auth}
            decision={deciding}
            onDecided={onDecided}
            onClose={() => setDeciding(null)}
            darkMode={darkMode}
          />
        )}
      </AnimatePresence>
    </motion.div>
  );
};

// ── Card de autorización resuelta ─────────────────────────────────
const ResolvedAuthCard = ({ auth, darkMode }) => {
  const { t } = useTranslation('admin');
  const [expanded, setExpanded] = useState(false);
  const observations = auth.observations || [];

  const statusConfig = {
    authorized:      { label: 'Autorizado',          color: '#16a34a', icon: '✅', bg: 'rgba(22,163,74,0.08)',  border: 'rgba(22,163,74,0.2)'  },
    retained:        { label: 'Retenido',             color: '#ef4444', icon: '🔴', bg: 'rgba(239,68,68,0.08)', border: 'rgba(239,68,68,0.2)' },
    pilot_override:  { label: t('flightauth_pilot_override'), color: '#d97706', icon: '⚠️', bg: 'rgba(217,119,6,0.08)', border: 'rgba(217,119,6,0.2)'  },
  };
  const cfg = statusConfig[auth.status] || statusConfig.authorized;

  return (
    <div style={{ borderRadius:'16px', border:`1px solid ${cfg.border}`, background: darkMode?`${cfg.bg}`:`${cfg.bg}`, overflow:'hidden', marginBottom:'8px' }}>
      <div style={{ padding:'12px 16px', display:'flex', alignItems:'center', gap:'12px', cursor:'pointer' }}
        onClick={() => setExpanded(e => !e)}>
        <span style={{ fontSize:'18px', flexShrink:0 }}>{cfg.icon}</span>
        <div style={{ flex:1, minWidth:0 }}>
          <div style={{ display:'flex', alignItems:'center', gap:'8px', flexWrap:'wrap' }}>
            <p style={{ margin:0, fontWeight:700, fontSize:'13px', color: darkMode?'white':'#0f172a' }}>{auth.pilot_name}</p>
            <span style={{ fontSize:'10px', fontWeight:700, padding:'2px 7px', borderRadius:'20px', background:`${cfg.color}15`, color: cfg.color, border:`1px solid ${cfg.color}30` }}>
              {cfg.label}
            </span>
          </div>
          <p style={{ margin:'2px 0 0', fontSize:'11px', color: darkMode?'rgba(255,255,255,0.35)':'#94a3b8' }}>
            {auth.aircraft_name}{auth.registration ? ` · ${auth.registration}` : ''} · {formatDateTime(auth.decided_at || auth.override_at || auth.created_at)}
          </p>
        </div>
        {expanded
          ? <ChevronUp  style={{ width:'14px', height:'14px', color: darkMode?'rgba(255,255,255,0.3)':'#94a3b8', flexShrink:0 }} />
          : <ChevronDown style={{ width:'14px', height:'14px', color: darkMode?'rgba(255,255,255,0.3)':'#94a3b8', flexShrink:0 }} />
        }
      </div>

      <AnimatePresence>
        {expanded && (
          <motion.div initial={{ height:0, opacity:0 }} animate={{ height:'auto', opacity:1 }} exit={{ height:0, opacity:0 }} transition={{ duration:0.15 }}
            style={{ overflow:'hidden' }}>
            <div style={{ padding:'0 16px 14px', borderTop: darkMode?'1px solid rgba(255,255,255,0.06)':'1px solid rgba(0,0,0,0.05)' }}>
              {observations.length > 0 && (
                <div style={{ marginTop:'12px' }}>
                  {observations.map((obs, i) => (
                    <div key={i} style={{ display:'flex', alignItems:'center', gap:'8px', padding:'5px 0', fontSize:'12px', color: darkMode?'rgba(255,255,255,0.6)':'#374151' }}>
                      <span>{obs.pilot_decision === 'can_fly' ? '✅' : '⚠️'}</span>
                      <span>{obs.text}</span>
                    </div>
                  ))}
                </div>
              )}
              {auth.instructor_note && (
                <div style={{ marginTop:'10px', padding:'8px 12px', borderRadius:'10px', background: darkMode?'rgba(255,255,255,0.04)':'rgba(0,0,0,0.03)', border: darkMode?'1px solid rgba(255,255,255,0.07)':'1px solid #f1f5f9' }}>
                  <p style={{ margin:'0 0 2px', fontSize:'10px', fontWeight:700, color: darkMode?'rgba(255,255,255,0.3)':'#94a3b8', textTransform:'uppercase', letterSpacing:'0.05em' }}>Nota del instructor</p>
                  <p style={{ margin:0, fontSize:'12px', color: darkMode?'rgba(255,255,255,0.7)':'#374151' }}>{auth.instructor_note}</p>
                </div>
              )}
              {auth.status === 'pilot_override' && (
                <p style={{ margin:'10px 0 0', fontSize:'11px', color:'#d97706', fontWeight:600 }}>
                  {t('flightauth_override_warning')}
                </p>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

// ── Componente principal ──────────────────────────────────────────
const AdminFlightAuth = ({ darkMode, userRole }) => {
  const { t } = useTranslation('admin');
  const [pending,  setPending]  = useState([]);
  const [resolved, setResolved] = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [error,    setError]    = useState('');
  const [tab,      setTab]      = useState('pending');
  const pollRef = useRef(null);

  const load = useCallback(async () => {
    setError('');
    try {
      const [pendingRes, allRes] = await Promise.all([
        api.get('/api/flight-auth/pending'),
        api.get('/api/flight-auth?limit=30').catch(() => ({ data: { data: [] } })),
      ]);
      setPending(pendingRes.data.data  || []);
      setResolved((allRes.data.data || []).filter(a => a.status !== 'pending'));
    } catch (err) {
      setError('Error al cargar las autorizaciones');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => {
    load();
    // Auto-refresh cada 15 segundos para ver nuevas solicitudes
    pollRef.current = setInterval(load, POLLING_INTERVALS.flight_auth); 
    return () => clearInterval(pollRef.current);
  }, [load]);

  const card = {
    background: darkMode ? 'rgba(255,255,255,0.03)' : 'white',
    border: darkMode ? '1px solid rgba(255,255,255,0.08)' : '1px solid #e2e8f0',
    borderRadius: '20px', padding: '24px',
    boxShadow: '0 2px 12px rgba(0,0,0,0.06)',
  };

  return (
    <div style={{ display:'flex', flexDirection:'column', gap:'24px' }}>
      <style>{`@keyframes spin { from{transform:rotate(0deg)} to{transform:rotate(360deg)} }`}</style>

      {/* Header */}
      <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', flexWrap:'wrap', gap:'12px' }}>
        <div>
          <h1 style={{ margin:'0 0 4px', fontSize:'24px', fontWeight:900, color: darkMode?'white':'#0f172a', letterSpacing:'-0.5px' }}>
            {t('flightauth_title')}
          </h1>
          <p style={{ margin:0, fontSize:'14px', color: darkMode?'rgba(255,255,255,0.4)':'#64748b' }}>
            {t('flightauth_subtitle')}
          </p>
        </div>
        <div style={{ display:'flex', gap:'10px', alignItems:'center' }}>
          {pending.length > 0 && (
            <motion.div animate={{ scale:[1,1.05,1] }} transition={{ duration:1.5, repeat:Infinity }}
              style={{ padding:'6px 14px', borderRadius:'20px', background:'rgba(239,68,68,0.1)', border:'1.5px solid rgba(239,68,68,0.3)', fontSize:'13px', fontWeight:800, color:'#ef4444', display:'flex', alignItems:'center', gap:'6px' }}>
              <motion.div animate={{ opacity:[1,0.3,1] }} transition={{ duration:1, repeat:Infinity }}
                style={{ width:'8px', height:'8px', borderRadius:'50%', background:'#ef4444', flexShrink:0 }} />
              {pending.length} pendiente{pending.length > 1 ? 's' : ''}
            </motion.div>
          )}
          <motion.button onClick={load} whileTap={{ scale:0.95 }}
            style={{ padding:'8px', borderRadius:'12px', border: darkMode?'1px solid rgba(255,255,255,0.1)':'1px solid #e2e8f0', background:'transparent', cursor:'pointer', color: darkMode?'rgba(255,255,255,0.5)':'#64748b', display:'flex' }}>
            <RefreshCw style={{ width:'16px', height:'16px' }} />
          </motion.button>
        </div>
      </div>

      {/* Stats */}
      <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(140px, 1fr))', gap:'12px' }}>
        {[
          { label:t('flightauth_pending'),   value: pending.length,                                    color:'#f59e0b', icon:'⏳' },
          { label:t('flightauth_authorized'),  value: resolved.filter(a=>a.status==='authorized').length, color:'#16a34a', icon:'✅' },
          { label:t('flightauth_retained'),    value: resolved.filter(a=>a.status==='retained').length,   color:'#ef4444', icon:'🔴' },
          { label:t('flightauth_commander'),   value: resolved.filter(a=>a.status==='pilot_override').length, color:'#d97706', icon:'✈️' },
        ].map((s, i) => (
          <div key={i} style={{ ...card, display:'flex', alignItems:'center', gap:'12px', padding:'16px' }}>
            <div style={{ width:'40px', height:'40px', borderRadius:'12px', background:`${s.color}15`, display:'flex', alignItems:'center', justifyContent:'center', fontSize:'18px', flexShrink:0 }}>{s.icon}</div>
            <div>
              <p style={{ margin:0, fontSize:'22px', fontWeight:900, color: darkMode?'white':'#0f172a', lineHeight:1 }}>{s.value}</p>
              <p style={{ margin:'2px 0 0', fontSize:'11px', fontWeight:600, color: darkMode?'rgba(255,255,255,0.35)':'#94a3b8' }}>{s.label}</p>
            </div>
          </div>
        ))}
      </div>

      {/* Tabs */}
      <div style={{ display:'flex', gap:'8px' }}>
        {[
          { id:'pending',  label:`⏳ ${t('flightauth_pending')}${pending.length > 0 ? ` (${pending.length})` : ''}` },
          { id:'resolved', label:'📋 Historial' },
        ].map(t => (
          <button key={t.id} onClick={() => setTab(t.id)}
            style={{ padding:'8px 18px', borderRadius:'12px', border:'none', cursor:'pointer', fontWeight:700, fontSize:'13px', fontFamily:'inherit', background: tab === t.id ? (t.id === 'pending' ? '#f59e0b' : '#2563eb') : (darkMode?'rgba(255,255,255,0.06)':'#f1f5f9'), color: tab === t.id ? 'white' : (darkMode?'rgba(255,255,255,0.5)':'#64748b'), transition:'all 0.2s' }}>
            {t.label}
          </button>
        ))}
      </div>

      {/* Contenido */}
      <div style={card}>
        {loading ? (
          <div style={{ display:'flex', justifyContent:'center', padding:'40px 0' }}>
            <Loader style={{ width:'28px', height:'28px', color:'#2563eb', animation:'spin 1s linear infinite' }} />
          </div>
        ) : error ? (
          <p style={{ margin:0, color:'#ef4444', textAlign:'center', padding:'20px' }}>{error}</p>
        ) : tab === 'pending' ? (
          pending.length === 0 ? (
            <div style={{ textAlign:'center', padding:'48px 0' }}>
              <div style={{ fontSize:'48px', marginBottom:'12px' }}>✅</div>
              <p style={{ margin:0, fontSize:'16px', fontWeight:700, color: darkMode?'white':'#0f172a' }}>{t('flightauth_no_pending')}</p>
              <p style={{ margin:'6px 0 0', fontSize:'13px', color: darkMode?'rgba(255,255,255,0.35)':'#94a3b8' }}>
                Cuando un piloto reporte anomalías vas a ver la solicitud acá
              </p>
            </div>
          ) : (
            pending.map(auth => (
              <AuthCard key={auth.id} auth={auth} onDecided={load} darkMode={darkMode} />
            ))
          )
        ) : (
          resolved.length === 0 ? (
            <div style={{ textAlign:'center', padding:'40px 0', color: darkMode?'rgba(255,255,255,0.3)':'#94a3b8' }}>
              <Plane style={{ width:'36px', height:'36px', margin:'0 auto 10px', display:'block', opacity:0.4 }} />
              <p style={{ margin:0, fontSize:'14px' }}>Sin historial todavía</p>
            </div>
          ) : (
            resolved.map(auth => (
              <ResolvedAuthCard key={auth.id} auth={auth} darkMode={darkMode} />
            ))
          )
        )}
      </div>

      <p style={{ margin:0, textAlign:'center', fontSize:'11px', color: darkMode?'rgba(255,255,255,0.18)':'#cbd5e1' }}>
        {t('flightauth_auto_update')}
      </p>
    </div>
  );
};

export default AdminFlightAuth;
